import { useId, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Lightbulb, Paperclip, Plus, X } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Rotulo } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, get, getTodos, mensagemDeErro, post } from '../../nucleo/api';
import { data, dataHora, dinheiro, nomeDeArquivo, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import type { Atividade, Oportunidade } from './tipos';
import { exemplosDeAta, exemplosEmMassa, inserirNoCursor, juntarExemplo, LACUNA, proximaLacuna, ultimosDaNegociacao, type EmailDaAta, type PropostaDaAta } from './crm-sugestoes';
import { FalhaNoBloco, porBlocos } from '@malgoos/shared';

/**
 * CRM no novo desenho, lote 2 (complemento 4 do clone do RD): concluir exige a ata. Exemplos pelo tipo da tarefa (com contato,
 * empresa e telefone), "Inserir do sistema" no ponto do cursor, documentos que já estão na negociação e anexos novos (opcionais).
 * Em massa, uma ata para todas. A ata vai para a atividade e, como nota, para o histórico da negociação (servidor).
 */

const ENTIDADE = 'pipeline.oportunidade';
export const CLASSE_AREA =
  'w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 py-2 text-base text-tinta-1 placeholder:text-tinta-3 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco md:text-sm';
const CLASSE_CHIP = 'inline-flex min-h-8 items-center gap-1.5 rounded-full border border-borda bg-sup-1 px-3 text-sm text-tinta-1 hover:bg-sup-2 aria-pressed:border-acento-forte aria-pressed:text-acento-forte';

interface AnexoChatter {
  id: string;
  nome: string;
  mime: string;
  tamanho: number;
}
interface MensagemChatter {
  id: string;
  tipo: string;
  corpo: string;
  created_at: string;
}
interface FichaParceiro {
  nome: string;
  telefone: string | null;
  email: string | null;
  contatos: { nome: string; cargo: string | null; email: string | null; telefone: string | null }[];
}

const tamanho = (b: number) => (b >= 1_048_576 ? `${(b / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

/** O que o CRM já sabe da negociação (só na conclusão de uma tarefa). */
function useContexto(a: Atividade | null) {
  const s = useSessao();
  const op = useQuery({ queryKey: ['pipeline', 'oportunidade', a?.oportunidade_id], queryFn: () => get<Oportunidade>(`/pipeline/oportunidades/${a!.oportunidade_id}`), enabled: !!a });
  const chatter = useQuery({
    queryKey: ['chatter', ENTIDADE, a?.oportunidade_id],
    queryFn: () => get<{ mensagens: MensagemChatter[]; anexos: AnexoChatter[] }>(`/chatter/${ENTIDADE}/${a!.oportunidade_id}`),
    enabled: !!a,
  });
  const atividades = useQuery({
    queryKey: ['pipeline', 'atividades-da-oportunidade', a?.oportunidade_id],
    queryFn: () => getTodos<Atividade>('/pipeline/atividades', { oportunidade_id: a!.oportunidade_id, filtro: 'todas' }),
    enabled: !!a,
  });
  const parceiro = useQuery({
    queryKey: ['pipeline', 'parceiro-da-ata', op.data?.parceiro_id],
    queryFn: () => get<FichaParceiro>(`/cadastros/parceiros/${op.data!.parceiro_id}`),
    enabled: !!op.data?.parceiro_id && s.pode('cadastros.ver'),
    retry: false,
  });
  // Rodada crm1 (SPEC-B1): "Última proposta" e "Último e-mail" pelas rotas da ficha; o endereço só vem com leitura do Cadastros.
  const propostas = useQuery({
    queryKey: ['pipeline', 'propostas', a?.oportunidade_id],
    queryFn: () => get<{ itens: PropostaDaAta[] }>(`/pipeline/oportunidades/${a!.oportunidade_id}/propostas`),
    enabled: !!a,
    retry: false,
  });
  const emails = useQuery({
    queryKey: ['pipeline', 'emails', a?.oportunidade_id],
    queryFn: () => get<{ itens: EmailDaAta[] }>(`/pipeline/oportunidades/${a!.oportunidade_id}/emails`),
    enabled: !!a,
    retry: false,
  });
  return {
    op: op.data,
    chatter: chatter.data,
    atividades: atividades.data?.itens ?? [],
    parceiro: parceiro.data,
    propostas: propostas.data?.itens ?? [],
    emails: emails.data?.itens ?? [],
  };
}

export function DialogoConcluir({ atividades, aoFechar, aoConcluir }: { atividades: Atividade[]; aoFechar: () => void; aoConcluir?: (r: { ata: string }) => void }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const id = useId();
  const area = useRef<HTMLTextAreaElement>(null);
  const arquivo = useRef<HTMLInputElement>(null);
  const pendentes = atividades.filter((a) => a.estado === 'pendente');
  const um = pendentes.length === 1 ? pendentes[0]! : null;
  const ctx = useContexto(um);
  const [ata, setAta] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [exemplo, setExemplo] = useState<number | null>(null);
  const [inseridos, setInseridos] = useState(new Set<string>());
  const [marcados, setMarcados] = useState(new Set<string>());
  const [novos, setNovos] = useState<File[]>([]);
  const [enviando, setEnviando] = useState(false);

  // Contato: o primeiro contato da empresa (cadastro) ou a própria pessoa, quando o cliente é pessoa.
  const p = ctx.parceiro;
  const contato = p?.contatos[0] ?? (p ? { nome: p.nome, cargo: null, email: p.email, telefone: p.telefone } : null);
  const empresa = ctx.op?.parceiro_nome ?? um?.parceiro_nome ?? null;
  const exemplos = um
    ? exemplosDeAta(t, { titulo: um.titulo, tipo: um.tipo, data: data(um.data_hora), contato: contato?.nome, empresa, telefone: contato?.telefone ?? p?.telefone, email: contato?.email ?? p?.email, etapa: ctx.op?.etapa_nome ?? um.oportunidade_etapa_nome })
    : exemplosEmMassa(t);

  const infos: { chave: string; rotulo: string; texto: string }[] = [];
  if (um && ctx.op) {
    const o = ctx.op;
    infos.push({ chave: 'negociacao', rotulo: t('crm.inserir.negociacao'), texto: t('crm.inserir.negociacaoTxt', { nome: o.titulo, etapa: o.etapa_nome, valor: dinheiro(o.valor_centavos, o.moeda) }) });
    if (empresa) infos.push({ chave: 'empresa', rotulo: t('crm.inserir.empresa'), texto: t('crm.inserir.empresaTxt', { nome: empresa }) });
    if (contato) {
      const extra = [contato.cargo ? ` (${contato.cargo})` : '', contato.email ? ` · ${contato.email}` : '', contato.telefone ? ` · ${contato.telefone}` : ''].join('');
      infos.push({ chave: 'contato', rotulo: t('crm.inserir.contato'), texto: t('crm.inserir.contatoTxt', { nome: contato.nome, extra }) });
    }
    if (o.itens?.length) {
      const lista = o.itens.map((i) => `${i.descricao} (${i.quantidade}x, ${dinheiro(i.preco_centavos * i.quantidade, o.moeda)})`).join('; ');
      infos.push({ chave: 'produtos', rotulo: t('crm.inserir.produtos'), texto: t('crm.inserir.produtosTxt', { lista }) });
    }
    infos.push(...ultimosDaNegociacao(t, { data, dinheiro }, ctx.propostas, ctx.emails));
    const nota = ctx.chatter?.mensagens.find((m) => m.tipo === 'nota' && !m.corpo.startsWith('Ata da tarefa'));
    if (nota) infos.push({ chave: 'anotacao', rotulo: t('crm.inserir.ultimaAnotacao'), texto: t('crm.inserir.anotacaoTxt', { data: data(nota.created_at), texto: nota.corpo }) });
    const anterior = ctx.atividades.filter((x) => x.estado === 'concluida' && x.ata && x.id !== um.id).sort((a, b) => (b.concluida_em ?? '').localeCompare(a.concluida_em ?? ''))[0];
    if (anterior) infos.push({ chave: 'ataAnterior', rotulo: t('crm.inserir.ataAnterior'), texto: t('crm.inserir.ataAnteriorTxt', { titulo: anterior.titulo, ata: anterior.ata }) });
    const reuniao = ctx.atividades.filter((x) => x.estado === 'pendente' && x.tipo === 'reuniao' && x.id !== um.id && Date.parse(x.data_hora) > Date.now()).sort((a, b) => a.data_hora.localeCompare(b.data_hora))[0];
    if (reuniao) infos.push({ chave: 'reuniao', rotulo: t('crm.inserir.proximaReuniao'), texto: t('crm.inserir.reuniaoTxt', { titulo: reuniao.titulo, data: dataHora(reuniao.data_hora) }) });
  }
  const anexosDaNegociacao = um ? (ctx.chatter?.anexos ?? []) : [];

  const marcarLacuna = (texto: string, desde = 0) => {
    const i = proximaLacuna(texto, desde);
    if (i < 0) return;
    requestAnimationFrame(() => {
      area.current?.focus();
      area.current?.setSelectionRange(i, i + LACUNA.length);
    });
  };
  const usarExemplo = (i: number) => {
    const novo = juntarExemplo(ata, exemplos[i]!.texto);
    setAta(novo);
    setExemplo(i);
    setErro(null);
    marcarLacuna(novo);
  };
  const inserir = (x: { chave: string; texto: string }) => {
    const el = area.current;
    const r = inserirNoCursor(ata, el?.selectionStart ?? ata.length, el?.selectionEnd ?? ata.length, x.texto);
    setAta(r.texto);
    setInseridos((s) => new Set(s).add(x.chave));
    requestAnimationFrame(() => {
      area.current?.focus();
      area.current?.setSelectionRange(r.cursor, r.cursor);
    });
  };

  const enviarAnexos = async (oportunidadeId: string) => {
    const ids: string[] = [];
    for (const f of novos) {
      const fd = new FormData();
      fd.append('entidade', ENTIDADE);
      fd.append('registro_id', oportunidadeId);
      fd.append('arquivo', f);
      ids.push((await api<{ id: string }>('POST', '/anexos', fd)).id);
    }
    return ids;
  };

  const concluir = async () => {
    const texto = ata.trim();
    if (texto.length < 10) return setErro(t('crm.concluir.erroCurta'));
    if (texto.includes(LACUNA)) {
      setErro(t('crm.concluir.erroLacuna'));
      return marcarLacuna(texto);
    }
    setEnviando(true);
    try {
      if (um) {
        const anexo_ids = [...marcados, ...(await enviarAnexos(um.oportunidade_id))];
        await post(`/pipeline/atividades/${um.id}/concluir`, { ata: texto, anexo_ids });
      } else {
        // Em massa: cada negociação recebe os arquivos novos (o servidor só cita na ata os anexos da própria negociação).
        const anexo_ids: string[] = [];
        for (const op of new Set(pendentes.map((a) => a.oportunidade_id))) anexo_ids.push(...(await enviarAnexos(op)));
        // Rodada crm1 (ROB-M1): até 50 por chamada.
        await porBlocos(pendentes.map((a) => a.id), (bloco) => post('/pipeline/atividades/lote/concluir', { ids: bloco, ata: texto, anexo_ids }));
      }
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['/pipeline/atividades'] }),
        qc.invalidateQueries({ queryKey: ['pipeline'] }),
        ...[...new Set(pendentes.map((a) => a.oportunidade_id))].map((op) => qc.invalidateQueries({ queryKey: ['chatter', ENTIDADE, op] })),
      ]);
      toast.sucesso(um ? t('crm.concluir.concluida') : t('crm.concluir.concluidas'));
      aoConcluir?.({ ata: texto });
      aoFechar();
    } catch (e) {
      // Rodada crm2 (ROB-M1): parte já foi concluída; a lista recarrega (as concluídas saem dos pendentes) e o diálogo fecha.
      if (e instanceof FalhaNoBloco) {
        await Promise.all([qc.invalidateQueries({ queryKey: ['/pipeline/atividades'] }), qc.invalidateQueries({ queryKey: ['pipeline'] })]);
        toast.erro(t('crm.contagem.parcial', { feitos: e.feitos.length, total: e.total, erro: mensagemDeErro(e.causa) }));
        aoFechar();
        return;
      }
      setErro(mensagemDeErro(e));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={um ? t('crm.concluir.titulo') : t('crm.concluir.tituloVarias', { count: pendentes.length })}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={enviando} onClick={() => void concluir()} data-concluir-tarefa="">
            {um ? t('crm.concluir.botao') : t('crm.concluir.botaoVarias')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4" data-dialogo-concluir="">
        <p className="text-sm text-tinta-2">{t('crm.concluir.intro')}</p>
        <div className="rounded-lg border border-borda bg-sup-1 px-3 py-2 text-sm">
          {um ? (
            <>
              <p className="font-semibold">{textoLivre(um.titulo, um.parceiro_nome)}</p>
              <p className="text-xs text-tinta-3">
                {textoLivre(um.oportunidade_titulo, um.parceiro_nome)} · <span className="numero">{dataHora(um.data_hora)}</span>
              </p>
            </>
          ) : (
            <>
              <ul className="flex flex-col gap-0.5">
                {pendentes.map((a) => (
                  <li key={a.id}>{textoLivre(a.titulo, a.parceiro_nome)}</li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-tinta-3">{t('crm.concluir.mesmaAta')}</p>
            </>
          )}
        </div>
        <div>
          <p className="mb-1.5 text-xs font-semibold text-tinta-2">{t('crm.concluir.exemplos')}</p>
          <div className="flex flex-wrap gap-2">
            {exemplos.map((x, i) => (
              <button key={x.rotulo} type="button" className={CLASSE_CHIP} aria-pressed={exemplo === i} onClick={() => usarExemplo(i)} data-exemplo-ata={i}>
                <Lightbulb className="size-3.5 shrink-0" aria-hidden />
                {x.rotulo}
              </button>
            ))}
          </div>
        </div>
        <Rotulo id={`${id}-ata`} rotulo={t('crm.concluir.ata')} obrigatorio erro={erro ?? undefined} ajuda={t('crm.concluir.ataAjuda')}>
          <textarea
            ref={area}
            id={`${id}-ata`}
            rows={6}
            value={ata}
            data-ata=""
            aria-invalid={!!erro || undefined}
            aria-describedby={erro ? `${id}-ata-erro` : `${id}-ata-ajuda`}
            placeholder={t('crm.concluir.ataPlaceholder')}
            className={CLASSE_AREA}
            onChange={(e) => {
              setAta(e.target.value);
              setErro(null);
            }}
            onKeyDown={(e) => {
              // Tab pula para o próximo "[ ]" enquanto houver algum (Shift+Tab sai do campo como sempre).
              if (e.key !== 'Tab' || e.shiftKey || !ata.includes(LACUNA)) return;
              e.preventDefault();
              marcarLacuna(ata, (area.current?.selectionEnd ?? 0));
            }}
          />
        </Rotulo>
        {infos.length > 0 && (
          <div>
            <p className="mb-1.5 text-xs text-tinta-3">
              <span className="font-semibold text-tinta-2">{t('crm.concluir.inserir')}</span> {t('crm.concluir.inserirAjuda')}
            </p>
            <div className="flex flex-wrap gap-2">
              {infos.map((x) => (
                <button key={x.chave} type="button" className={CLASSE_CHIP} aria-pressed={inseridos.has(x.chave)} title={textoLivre(x.texto, empresa, contato?.nome)} onClick={() => inserir(x)} data-inserir-ata={x.chave}>
                  <Plus className="size-3.5 shrink-0" aria-hidden />
                  {x.rotulo}
                </button>
              ))}
            </div>
          </div>
        )}
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1.5 text-sm font-medium text-tinta-2">
            {t('crm.concluir.arquivos')} <span className="font-normal text-tinta-3">{t('crm.concluir.opcional')}</span>
          </legend>
          {anexosDaNegociacao.length > 0 && (
            <div className="rounded-lg border border-borda p-3">
              <p className="text-xs font-semibold text-tinta-2">{t('crm.concluir.naNegociacao')}</p>
              <p className="mt-1 text-xs text-tinta-3">{t('crm.concluir.grupoArquivos')}</p>
              <ul className="mt-1 flex flex-col gap-1">
                {anexosDaNegociacao.map((a) => (
                  <li key={a.id}>
                    <label className="flex min-h-8 items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        data-anexo-existente={a.id}
                        checked={marcados.has(a.id)}
                        onChange={(e) =>
                          setMarcados((s) => {
                            const n = new Set(s);
                            if (e.target.checked) n.add(a.id);
                            else n.delete(a.id);
                            return n;
                          })
                        }
                      />
                      <Paperclip className="size-3.5 shrink-0 text-tinta-3" aria-hidden />
                      <span className="min-w-0 truncate" title={nomeDeArquivo(a.nome, empresa, contato?.nome)}>{nomeDeArquivo(a.nome, empresa, contato?.nome)}</span>
                      <span className="shrink-0 text-xs text-tinta-3">{tamanho(a.tamanho)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Botao tamanho="sm" icone={<Paperclip className="size-4" />} onClick={() => arquivo.current?.click()}>
              {t('crm.concluir.anexarNovo')}
            </Botao>
            <input
              ref={arquivo}
              type="file"
              multiple
              hidden
              data-anexo-novo=""
              onChange={(e) => {
                const fs = [...(e.target.files ?? [])];
                setNovos((x) => [...x, ...fs]);
                e.target.value = '';
              }}
            />
            {novos.map((f, i) => (
              <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 rounded-full bg-sup-2 px-2.5 py-1 text-xs">
                <Paperclip className="size-3" aria-hidden />
                {f.name} <span className="text-tinta-3">{tamanho(f.size)}</span>
                <button type="button" aria-label={t('crm.concluir.removerArquivo', { nome: f.name })} className="rounded p-0.5 hover:bg-sup-3" onClick={() => setNovos((x) => x.filter((_, j) => j !== i))}>
                  <X className="size-3" aria-hidden />
                </button>
              </span>
            ))}
          </div>
        </fieldset>
      </div>
    </Modal>
  );
}
